'use client'

import * as React from 'react'
import { Wallet, ArrowDownRight, DollarSign, ArrowUpRight, TrendingUp } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card'
import { Progress } from 'components/ui/progress'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table'
import { cn } from 'lib/utils'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Line,
  PieChart,
  Pie,
  Cell,
} from 'recharts'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '../../../firebase'

const categoryColors = [
  'oklch(0.35 0.12 145)',
  'oklch(0.87 0.15 85)',
  'oklch(0.55 0.16 150)',
  'oklch(0.7 0.17 65)',
  'oklch(0.577 0.245 27.325)',
  'oklch(0.48 0.03 145)',
]

const parseAmount = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const sanitized = value.replace(/[^0-9.-]+/g, '')
    const parsed = Number(sanitized)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

const formatCompactKES = (value) => {
  const amount = parseAmount(value)
  if (amount >= 1_000_000_000) return `KES ${(amount / 1_000_000_000).toFixed(1)}B`
  if (amount >= 1_000_000) return `KES ${(amount / 1_000_000).toFixed(1)}M`
  if (amount >= 1_000) return `KES ${(amount / 1_000).toFixed(1)}K`
  return `KES ${Math.round(amount)}`
}

const toDateValue = (value) => {
  if (!value) return null
  if (typeof value?.toDate === 'function') return value.toDate()
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1)
const addMonths = (date, count) => new Date(date.getFullYear(), date.getMonth() + count, 1)
const formatMonthLabel = (date) => date.toLocaleDateString('en-KE', { month: 'short', year: '2-digit' })

const getCommittedAmount = (expense) => {
  const explicit = parseAmount(expense.committedAmount || expense.committed || 0)
  if (explicit > 0) return explicit
  const status = String(expense.status || '').toLowerCase()
  if (status === 'committed' || status === 'pending') return parseAmount(expense.amount || 0)
  if (expense.isCommitted) return parseAmount(expense.amount || 0)
  return 0
}

const getActualAmount = (expense) => {
  const status = String(expense.status || '').toLowerCase()
  if (status === 'committed' || status === 'pending') return 0
  return parseAmount(expense.amount || 0)
}

const sumBudgetItems = (items = []) =>
  items.reduce((sum, item) => {
    const total = parseAmount(item.total)
    if (total > 0) return sum + total
    return sum + parseAmount(item.unitCost) * parseAmount(item.quantity)
  }, 0)

export default function FinancePage() {
  const [projectFilter, setProjectFilter] = React.useState('all')
  const [projects, setProjects] = React.useState([])
  const [budgetCategories, setBudgetCategories] = React.useState([])
  const [expenditures, setExpenditures] = React.useState([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    let isMounted = true

    async function loadFinance() {
      try {
        const [projectSnap, categorySnap, expenseSnap] = await Promise.all([
          getDocs(collection(db, 'projects')),
          getDocs(collection(db, 'budgetCategories')),
          getDocs(collection(db, 'expenditures')),
        ])
        if (!isMounted) return
        setProjects(projectSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
        setBudgetCategories(categorySnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
        setExpenditures(expenseSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
      } catch {
        if (!isMounted) return
        setProjects([])
        setBudgetCategories([])
        setExpenditures([])
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadFinance()

    return () => {
      isMounted = false
    }
  }, [])

  const projectMap = React.useMemo(
    () =>
      projects.reduce((acc, project) => {
        acc[project.id] = project
        return acc
      }, {}),
    [projects],
  )

  const filteredProjectIds = React.useMemo(() => {
    if (projectFilter === 'all') return projects.map((project) => project.id)
    return [projectFilter]
  }, [projectFilter, projects])

  const filteredExpenditures = expenditures.filter((expense) =>
    filteredProjectIds.includes(expense.projectId),
  )

  const categoriesByProject = budgetCategories.reduce((acc, category) => {
    if (!category.projectId) return acc
    if (!acc[category.projectId]) acc[category.projectId] = []
    acc[category.projectId].push(category)
    return acc
  }, {})

  const getProjectBudget = (projectId) => {
    const categories = categoriesByProject[projectId] || []
    const approvedSum = categories.reduce((sum, cat) => sum + parseAmount(cat.approved || cat.total), 0)
    if (approvedSum > 0) return approvedSum
    const project = projectMap[projectId]
    if (parseAmount(project?.totalBudget) > 0) return parseAmount(project.totalBudget)
    if (Array.isArray(project?.budgetItems)) return sumBudgetItems(project.budgetItems)
    return 0
  }

  const expendituresByProject = filteredExpenditures.reduce((acc, expense) => {
    if (!expense.projectId) return acc
    acc[expense.projectId] = acc[expense.projectId] || { actual: 0, committed: 0 }
    acc[expense.projectId].actual += getActualAmount(expense)
    acc[expense.projectId].committed += getCommittedAmount(expense)
    return acc
  }, {})

  const projectsToShow =
    projectFilter === 'all' ? projects : projects.filter((project) => project.id === projectFilter)

  const budgetByProjectData = projectsToShow.map((project) => {
    const budgeted = getProjectBudget(project.id)
    const actual = expendituresByProject[project.id]?.actual || 0
    const committed = expendituresByProject[project.id]?.committed || 0
    return {
      name: project.code || project.shortCode || project.title || 'Project',
      budgeted,
      actual,
      committed,
    }
  })

  const totalBudgeted = budgetByProjectData.reduce((sum, item) => sum + item.budgeted, 0)
  const totalActual = budgetByProjectData.reduce((sum, item) => sum + item.actual, 0)
  const totalCommitted = budgetByProjectData.reduce((sum, item) => sum + item.committed, 0)
  const available = totalBudgeted - totalActual - totalCommitted
  const burnRate = totalBudgeted > 0 ? Math.round((totalActual / totalBudgeted) * 100) : 0

  const categorySpend = filteredExpenditures.reduce((acc, expense) => {
    const key = expense.category || 'Other'
    acc[key] = (acc[key] || 0) + getActualAmount(expense)
    return acc
  }, {})

  const categorySpendData = Object.entries(categorySpend).map(([name, value]) => ({ name, value }))

  const categoryRows = projectsToShow.flatMap((project) => {
    const categories = categoriesByProject[project.id] || []
    if (categories.length > 0) {
      return categories.map((cat) => ({
        projectId: project.id,
        projectLabel: project.code || project.shortCode || project.title || 'Project',
        category: cat.category || 'Other',
        approved: parseAmount(cat.approved || cat.total),
      }))
    }
    if (Array.isArray(project.budgetItems) && project.budgetItems.length > 0) {
      return project.budgetItems.map((item, index) => ({
        id: item.id || `${project.id}-fallback-${index}`,
        projectId: project.id,
        projectLabel: project.code || project.shortCode || project.title || 'Project',
        category: item.category || 'Other',
        approved: parseAmount(item.total) || parseAmount(item.unitCost) * parseAmount(item.quantity),
      }))
    }
    return []
  })

  const expenditureByCategory = filteredExpenditures.reduce((acc, expense) => {
    const key = `${expense.projectId}::${expense.category || 'Other'}`
    if (!acc[key]) acc[key] = { actual: 0, committed: 0 }
    acc[key].actual += getActualAmount(expense)
    acc[key].committed += getCommittedAmount(expense)
    return acc
  }, {})

  const budgetLineRows = categoryRows.map((row) => {
    const key = `${row.projectId}::${row.category}`
    const spent = expenditureByCategory[key]?.actual || 0
    const committed = expenditureByCategory[key]?.committed || 0
    const util = row.approved > 0 ? Math.round(((spent + committed) / row.approved) * 100) : 0
    return {
      ...row,
      spent,
      committed,
      util,
    }
  })

  const now = new Date()
  const months = Array.from({ length: 6 }, (_, index) => startOfMonth(addMonths(now, index - 5)))
  const monthlyPlanned = totalBudgeted > 0 ? totalBudgeted / 12 : 0

  const cashFlowData = months.map((month) => {
    const monthStart = startOfMonth(month)
    const monthKey = `${monthStart.getFullYear()}-${monthStart.getMonth()}`
    const monthActual = filteredExpenditures.reduce((sum, expense) => {
      const date = toDateValue(expense.date || expense.createdAt)
      if (!date) return sum
      const key = `${date.getFullYear()}-${date.getMonth()}`
      if (key !== monthKey) return sum
      return sum + getActualAmount(expense)
    }, 0)
    const monthCommitted = filteredExpenditures.reduce((sum, expense) => {
      const date = toDateValue(expense.date || expense.createdAt)
      if (!date) return sum
      const key = `${date.getFullYear()}-${date.getMonth()}`
      if (key !== monthKey) return sum
      return sum + getCommittedAmount(expense)
    }, 0)
    return {
      month: formatMonthLabel(monthStart),
      planned: monthlyPlanned,
      actual: monthActual,
      committed: monthCommitted,
    }
  })

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">Financial Monitor</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Budget tracking, expenditure analysis, and cash flow monitoring across the portfolio
          </p>
        </div>
        <Select value={projectFilter} onValueChange={setProjectFilter}>
          <SelectTrigger className="w-[200px] h-9">
            <SelectValue placeholder="All Projects" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Projects</SelectItem>
            {projects.map((project) => (
              <SelectItem key={project.id} value={project.id}>
                {(project.code || project.shortCode || project.title || project.id).slice(0, 35)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="size-4 text-primary" />
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Total Budget</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{loading ? '—' : formatCompactKES(totalBudgeted)}</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <ArrowDownRight className="size-4 text-destructive" />
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Spent</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{loading ? '—' : formatCompactKES(totalActual)}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">{burnRate}% burn rate</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-destructive" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="size-4 text-warning" />
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Committed</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{loading ? '—' : formatCompactKES(totalCommitted)}</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-warning" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <ArrowUpRight className="size-4 text-success" />
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Available</span>
            </div>
            <p className="text-2xl font-bold text-success">{loading ? '—' : formatCompactKES(available)}</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-success" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="size-4 text-primary" />
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Utilisation</span>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {loading ? '—' : totalBudgeted > 0 ? `${Math.round(((totalActual + totalCommitted) / totalBudgeted) * 100)}%` : '0%'}
            </p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary" />
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground">Monthly Spend vs Plan</CardTitle>
            <CardDescription className="text-xs">Planned is average budget spread across the year</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={cashFlowData} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                <YAxis tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" tickFormatter={(v) => formatCompactKES(v)} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} formatter={(v) => formatCompactKES(v)} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="actual" fill="oklch(0.577 0.245 27.325)" name="Actual Spend" radius={[4, 4, 0, 0]} />
                <Bar dataKey="committed" fill="oklch(0.87 0.15 85)" name="Committed" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="planned" stroke="oklch(0.35 0.12 145)" strokeWidth={2} name="Planned (Avg)" dot={false} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground">Budget vs Actual by Project</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={budgetByProjectData} barSize={14}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
                <XAxis dataKey="name" tick={{ fontSize: 9 }} stroke="oklch(0.48 0.03 145)" />
                <YAxis tick={{ fontSize: 9 }} stroke="oklch(0.48 0.03 145)" tickFormatter={(v) => formatCompactKES(v)} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} formatter={(v) => formatCompactKES(v)} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="budgeted" fill="oklch(0.35 0.12 145)" name="Budgeted" radius={[4, 4, 0, 0]} />
                <Bar dataKey="actual" fill="oklch(0.87 0.15 85)" name="Actual" radius={[4, 4, 0, 0]} />
                <Bar dataKey="committed" fill="oklch(0.48 0.03 145)" name="Committed" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground">Spending by Category</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={categorySpendData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={3} strokeWidth={0}>
                  {categorySpendData.map((_, i) => (
                    <Cell key={i} fill={categoryColors[i % categoryColors.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} formatter={(v) => formatCompactKES(v)} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-2">
              {categorySpendData.map((entry, i) => (
                <div key={entry.name} className="flex items-center gap-1.5">
                  <span className="size-2 rounded-full" style={{ backgroundColor: categoryColors[i % categoryColors.length] }} />
                  <span className="text-[10px] text-muted-foreground">{entry.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Budget Utilisation Table */}
        <Card className="border border-border lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground">Budget Line Utilisation</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Project</TableHead>
                  <TableHead className="text-xs">Category</TableHead>
                  <TableHead className="text-xs text-right">Budgeted</TableHead>
                  <TableHead className="text-xs text-right">Actual</TableHead>
                  <TableHead className="text-xs text-right">Committed</TableHead>
                  <TableHead className="text-xs">Utilisation</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                      Loading financial records...
                    </TableCell>
                  </TableRow>
                ) : budgetLineRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                      No budget lines found.
                    </TableCell>
                  </TableRow>
                ) : (
                  budgetLineRows.slice(0, 12).map((row, index) => (
                    <TableRow key={`${row.projectId}-${row.category}-${index}`}>
                      <TableCell className="text-xs text-foreground font-medium">{row.projectLabel}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{row.category}</TableCell>
                      <TableCell className="text-xs text-right text-foreground">{formatCompactKES(row.approved)}</TableCell>
                      <TableCell className="text-xs text-right text-foreground">{formatCompactKES(row.spent)}</TableCell>
                      <TableCell className="text-xs text-right text-muted-foreground">{formatCompactKES(row.committed)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 min-w-[80px]">
                          <Progress value={Math.min(row.util, 100)} className="h-1.5 flex-1" />
                          <span
                            className={cn(
                              'text-[10px] font-semibold',
                              row.util > 90 ? 'text-destructive' : row.util > 70 ? 'text-warning' : 'text-success',
                            )}
                          >
                            {row.util}%
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
